import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { DSBackLink } from '../../design-system/primitives';
import { authService } from '../../services/authService';
import { postJson } from './scorekeepingApi';
import PublicHeader from './PublicHeader';
import './scorekeeping.css';

/**
 * P4. Creating an account, standing at a machine.
 *
 * Two fields. The Stern username came from the score they just posted and is
 * shown rather than asked for again, which is the whole reason this can be two
 * fields instead of three.
 *
 * No verification email. That is a considered trade: verifying means a link, a
 * return trip and a password screen, at the exact moment the club is trying to
 * convert a stranger holding a pinball. Some addresses will be mistyped, and the
 * remedy is the account merge an admin already has.
 *
 * The one cheap mitigation the designer asked for is here: the address is echoed
 * back in plain text on the way out, while the person is still at the machine and
 * can still fix it.
 */

function ScoreSignup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const sternUsername = params.get('name') || '';
  const machineId = params.get('machine');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const passwordShort = password.length > 0 && password.length < 8;
  const ready = firstName.trim() && lastName.trim()
    && email.includes('@') && password.length >= 8 && !submitting;

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setErrorCode(null);

    try {
      const data = await postJson('/signup', {
        email: email.trim(),
        password,
        sternUsername,
        firstName: firstName.trim(),
        lastName: lastName.trim()
      });

      // They are signed in from here. No confirmation step, no return trip.
      // This has to go through authService rather than writing the storage keys
      // directly: the application shell keeps its own authentication state, and
      // setSession is what tells it, so the header, menu and bell appear.
      authService.setSession(data.token, data.user);
      setDone(data);
    } catch (err) {
      setError(err.message || 'That did not work. Check your signal and try again.');
      setErrorCode(err.code || null);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="sk-page">
        <div className="sk-shell">
          <div className="sk-confirm">
            <span className="sk-confirm__tick">✓</span>
            <h1 className="sk-h1">Account created</h1>
            <p className="sk-confirm__sub">Welcome, {firstName.trim() || sternUsername}</p>
          </div>

          <section className="sk-card">
            <p className="sk-eyebrow">Claimed onto your account</p>
            <p className="sk-signup__claimed">
              {done.claimed === 0
                ? 'Everything you post from here builds on this account.'
                : `${done.claimed} score${done.claimed === 1 ? '' : 's'} from this month `
                  + `${done.claimed === 1 ? 'is' : 'are'} now yours.`}
            </p>
            <p className="sk-muted">Earlier months stay as they were recorded.</p>
          </section>

          {/* The address echoed back while they are still standing here. A
              mistyped address is otherwise only discovered when they cannot sign
              in, which is weeks later and somebody else's problem to unpick. */}
          <section className="sk-card sk-echo">
            <p className="sk-eyebrow">Your email address</p>
            <p className="sk-echo__value">{email.trim()}</p>
            <p className="sk-muted">
              Not right?{' '}
              <Link to="/my-membership">Change it now</Link> while you&apos;re here.
            </p>
          </section>

          <div className="sk-actions">
            <button type="button" className="sk-cta" onClick={() => navigate('/scorekeeping/leaderboard')}>
              See where I stand
            </button>
            {machineId && (
              <Link className="sk-link" to={`/scorekeeping/machines/${machineId}/submit`}>
                Submit another score
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sk-page">
      <PublicHeader signInRedirect="/scorekeeping/leaderboard" />

      <form className="sk-shell" onSubmit={submit}>
        {/* Back goes through history because this page is reached from a score
            just posted and from the machine page, and both should return where
            they came from. */}
        <DSBackLink tone="subtle" size="sm" onClick={() => navigate(-1)}>Back</DSBackLink>
        <h1 className="sk-h1">Create your account</h1>
        <p className="sk-muted">
          You get your score history, your month-by-month progress, and comparison against the
          people you play with.
        </p>

        {sternUsername && (
          <div className="sk-locked">
            <p className="sk-eyebrow">Your Stern username</p>
            <p className="sk-locked__name">{sternUsername}</p>
            <p className="sk-muted">
              From the score you just posted. This month&apos;s scores under this name will move onto
              your account.
            </p>
          </div>
        )}

        {/* The club asks everyone creating an account for a name, and an account
            made at a machine is not a lesser account. Without it the profile and
            the membership card render blank, and a membership application later
            has no name to record or to check a signature against. */}
        <div className="sk-field">
          <label htmlFor="sk-first">First name</label>
          <input
            id="sk-first"
            className="sk-input"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Paula"
          />
        </div>

        <div className="sk-field">
          <label htmlFor="sk-last">Last name</label>
          <input
            id="sk-last"
            className="sk-input"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Nowak"
          />
        </div>

        {error && (
          <div className="sk-banner sk-banner--error">
            <span>{error}</span>
            {errorCode === 'EMAIL_IN_USE' && (
              <Link className="sk-btn sk-btn--small" to="/login">Sign in instead</Link>
            )}
          </div>
        )}

        <div className="sk-field">
          <label htmlFor="sk-email">Email address</label>
          <input
            id="sk-email"
            className="sk-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="sk-field">
          <label htmlFor="sk-password">Password</label>
          <div className="sk-input-row">
            <input
              id="sk-password"
              className={`sk-input ${passwordShort ? 'has-error' : ''}`}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
            <button type="button" className="sk-link" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {passwordShort && (
            <p className="sk-field__error">
              {8 - password.length} more character{8 - password.length === 1 ? '' : 's'} needed.
            </p>
          )}
        </div>

        <button type="submit" className="sk-cta" disabled={!ready}>
          {submitting ? 'Creating…' : 'Create account'}
        </button>
        <p className="sk-cta__note">
          {ready
            ? "No email to confirm. You're in as soon as you tap."
            : 'Your name, an email address and a password of at least 8 characters.'}
        </p>
      </form>
    </div>
  );
}

export default ScoreSignup;
